import React, {FC, useState, useEffect} from 'react'
import {Modal, Button, Form, Table, Alert} from 'react-bootstrap'
import Select, {SingleValue} from 'react-select'
import Swal from 'sweetalert2'
import {discordApiService, DiscordChannelMapping, DiscordChannel, DiscordGuild} from '../services/discordApi'
import {SelectAutoComplete} from '../../../../_metronic/partials/components/Select/SelectAutoComplete'
import { useSearchParams } from 'react-router-dom'
import { CreateProjectAndClient } from '../../../../_metronic/partials/modals/create-project-and-client/CreateProjectAndClientModal'

interface ChannelMappingForm {
  id: number | null // null para novos, number para existentes
  channelId: string
  channelName: string
  projectId: number | null
  projectName: string
  taskCreationMode: 'manual' | 'suggest'
}

interface DiscordManageModalProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
  guildName: string
  mappedChannelsCount: number
}

const DiscordManageModal: FC<DiscordManageModalProps> = ({
  show,
  onClose,
  onSuccess,
  guildName,
  mappedChannelsCount
}) => {
  const [mappings, setMappings] = useState<ChannelMappingForm[]>([])
  const [existingMappings, setExistingMappings] = useState<DiscordChannelMapping[]>([])
  const [channels, setChannels] = useState<DiscordChannel[]>([])
  const [guilds, setGuilds] = useState<DiscordGuild[]>([])
  const [selectedGuildId, setSelectedGuildId] = useState<string>('')
  const [selectedGuildName, setSelectedGuildName] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextId, setNextId] = useState(1)
  const [showProjectAndClientModal, setShowProjectAndClientModal] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useSearchParams()

  // Carrega dados quando o modal abre
  useEffect(() => {
    if (show) {
      loadData()
    }
  }, [show])

  const loadData = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      // Carrega status para obter guild_id
      const status = await discordApiService.getStatus()
      const guildId = status.integration?.guild_id

      // Carrega guilds (servidores) primeiro
      try {
        const guildsData = await discordApiService.getGuilds()
        setGuilds(guildsData)
        
        // Se já tem um servidor salvo, seleciona e carrega canais
        if (guildId) {
          setSelectedGuildId(guildId)
          // Busca o nome do servidor na lista de guilds
          const savedGuild = guildsData.find(g => g.id === guildId)
          if (savedGuild) {
            setSelectedGuildName(savedGuild.name)
          } else {
            // Se não encontrou na lista, tenta usar o nome do status
            const statusGuildName = status.integration?.guild_name
            if (statusGuildName) {
              setSelectedGuildName(statusGuildName)
            }
          }
          // Carrega canais do servidor
          try {
            const channelsData = await discordApiService.getChannels(guildId)
            // Garante que seja um array antes de filtrar
            if (Array.isArray(channelsData)) {
              // O backend já retorna apenas canais de texto, mas garantimos aqui também
              // Aceita tanto string 'text' quanto número 0 (tipo de canal de texto no Discord)
              const textChannels = channelsData.filter(c => {
                if (!c || !c.id || !c.name) return false
                // Tipo 0 = text channel no Discord
                const channelType = c.type
                return channelType === 'text' || channelType === 0
              })
              setChannels(textChannels)
                setError(null) // Limpa erro se carregou com sucesso
              
              // Log para debug
              if (textChannels.length === 0 && channelsData.length > 0) {
                console.warn('Nenhum canal de texto encontrado. Canais recebidos:', channelsData)
              }
            } else {
              console.error('Channels data is not an array:', channelsData)
              setError('Formato de resposta inválido ao carregar canais')
              setChannels([])
            }
          } catch (err: any) {
            console.error('Erro ao carregar canais:', err)
            const errorMessage = err.response?.data?.message || err.message || 'Erro ao carregar canais do servidor'
            setError(errorMessage)
            setChannels([]) // Limpa canais em caso de erro
          }
        } else {
          // Se não tem servidor salvo, não mostra erro - usuário escolherá no dropdown
          setError(null)
        }
      } catch (err: any) {
        console.error('Erro ao carregar servidores:', err)
        setError('Erro ao carregar servidores Discord. Verifique se a conexão foi concluída corretamente.')
      }

      // Carrega mapeamentos existentes
      try {
        const mappingsData = await discordApiService.getChannelMappings()
        setExistingMappings(mappingsData)
        
        // Converte para formato do formulário
        const formMappings: ChannelMappingForm[] = mappingsData.map(m => {
          return {
            id: m.id,
            channelId: m.channel_id,
            channelName: m.channel_name,
            projectId: m.project_id,
            projectName: m.project_name || '',
            taskCreationMode: m.task_creation_mode
          }
        })
        setMappings(formMappings)
        
        // Atualiza nextId para evitar conflitos
        if (formMappings.length > 0) {
          const maxId = Math.max(...formMappings.map(m => m.id || 0))
          setNextId(maxId + 1)
        }
      } catch (err) {
        console.error('Erro ao carregar mapeamentos:', err)
      }
    } catch (err: any) {
      console.error('Erro ao carregar dados:', err)
      setError(err.response?.data?.message || 'Erro ao carregar dados da integração')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGuildChange = async (guildId: string) => {
    const previousGuildId = selectedGuildId
    
    // Se está alterando o servidor e há mapeamentos existentes, solicita confirmação
    if (previousGuildId && previousGuildId !== guildId && (mappings.length > 0 || existingMappings.length > 0)) {
      const selectedGuild = guilds.find(g => g.id === guildId)
      const guildName = selectedGuild ? selectedGuild.name : guildId
      
      // Obtém o tema atual
      const CurrentTheme = window.localStorage.getItem('kt_theme_mode_value') || ''
      const tema = CurrentTheme === 'dark' ? '#353b48' : '#fff'
      
      // Configura SweetAlert com botões do Bootstrap
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-danger',
        },
        buttonsStyling: false,
      })
      
      const result = await swalWithBootstrapButtons.fire({
        title: 'Alterar servidor Discord?',
        html: `
          <div class="text-start">
            <p class="mb-3">Tem certeza que deseja alterar o servidor Discord para <strong>"${guildName}"</strong>?</p>
            <div class="alert alert-warning mb-0">
              <i class="fas fa-exclamation-triangle me-2"></i>
              <strong>ATENÇÃO:</strong> Todos os mapeamentos de canais atuais serão perdidos e você precisará criar novos mapeamentos para o novo servidor.
            </div>
          </div>
        `,
        icon: 'warning',
        background: tema,
        showCancelButton: true,
        confirmButtonText: 'Sim, alterar servidor',
        cancelButtonText: 'Não, cancelar',
        reverseButtons: true,
      })
      
      if (!result.isConfirmed) {
        // Usuário cancelou - não atualiza o estado, mantendo o valor anterior
        // O Select é controlado, então o React vai manter o valor correto
        return
      }
    }
    
    setSelectedGuildId(guildId)
    setIsLoading(true)
    setError(null)
    try {
      // Salva o servidor selecionado
      const selectedGuild = guilds.find(g => g.id === guildId)
      if (selectedGuild) {
        setSelectedGuildName(selectedGuild.name)
        try {
          await discordApiService.updateSettings({}, guildId, selectedGuild.name)
          
          // Se o servidor mudou, limpa os mapeamentos (o backend já os deletou)
          if (previousGuildId && previousGuildId !== guildId) {
            setMappings([])
            setExistingMappings([])
            setNextId(1)
          }
        } catch (err) {
          console.warn('Erro ao salvar servidor selecionado:', err)
          // Continua mesmo se falhar ao salvar
        }
      }

      // Carrega canais do servidor
      const channelsData = await discordApiService.getChannels(guildId)
      // Garante que seja um array antes de filtrar
      if (Array.isArray(channelsData)) {
        // O backend já retorna apenas canais de texto, mas garantimos aqui também
        // Aceita tanto string 'text' quanto número 0 (tipo de canal de texto no Discord)
        const textChannels = channelsData.filter(c => {
          if (!c || !c.id || !c.name) return false
          // Tipo 0 = text channel no Discord
          const channelType = c.type
          return channelType === 'text' || channelType === 0
        })
        setChannels(textChannels)
          setError(null) // Limpa erro se carregou com sucesso
      } else {
        console.error('Channels data is not an array:', channelsData)
        setError('Formato de resposta inválido ao carregar canais')
        setChannels([])
      }
    } catch (err: any) {
      console.error('Erro ao carregar canais:', err)
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao carregar canais do servidor'
      setError(errorMessage)
      setChannels([]) // Limpa canais em caso de erro
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddMapping = () => {
    const newMapping: ChannelMappingForm = {
      id: -nextId, // ID temporário negativo para novos (não salvo no banco)
      channelId: '',
      channelName: '',
      projectId: null,
      projectName: '',
      taskCreationMode: 'manual'
    }
    setMappings([...mappings, newMapping])
    setNextId(nextId + 1)
  }

  const handleRemoveMapping = (id: number | null) => {
    setMappings(mappings.filter(m => m.id !== id))
  }

  const handleMappingChange = (id: number | null, field: keyof ChannelMappingForm, value: any) => {
    setMappings(mappings.map(m => {
      if (m.id === id) {
        if (field === 'channelId') {
          const channel = channels.find(c => c.id === value)
          return {
            ...m,
            channelId: value,
            channelName: channel ? channel.name : ''
          }
        } else if (field === 'projectId') {
          // value vem do SelectAutoComplete como {value?: string, label: string} ou null/undefined
          if (!value || (typeof value === 'object' && (!('value' in value) || !value.value))) {
            return {
              ...m,
              projectId: null,
              projectName: ''
            }
          }
          const projectValue = typeof value === 'object' && value !== null && 'value' in value && value.value
            ? (typeof value.value === 'string' ? parseInt(value.value) : value.value)
            : null
          const projectLabel = typeof value === 'object' && value !== null && 'label' in value
            ? (value.label || '')
            : ''
          const projectId: number | null = projectValue && !isNaN(Number(projectValue)) ? Number(projectValue) : null
          return {
            ...m,
            projectId: projectId,
            projectName: projectLabel
          }
        } else {
          return {...m, [field]: value}
        }
      }
      return m
    }))
  }

  // Retorna canais disponíveis para um mapping específico (não mapeados + o atual)
  const getAvailableChannelsForMapping = (mappingId: number | null, currentChannelId: string) => {
    if (!channels || !Array.isArray(channels)) return []
    
    // IDs dos canais já mapeados (exceto o atual)
    const mappedChannelIds = mappings
      .filter(m => m.id !== mappingId && m.channelId) // Exclui o próprio mapping
      .map(m => m.channelId)
    
    // Retorna apenas canais não mapeados + o canal atual (se houver)
    return channels.filter(c => 
      !mappedChannelIds.includes(c.id) || c.id === currentChannelId
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedGuildId) {
      setError('Selecione um servidor Discord')
      return
    }

    setIsSubmitting(true)
    setError(null)
    
    try {
      // Processa cada mapeamento
      const validMappings = mappings.filter(m => m.channelId && m.projectId)
      
      // Remove mapeamentos que foram deletados (existem no backend mas não no form)
      const existingIds = mappings.filter(m => m.id !== null && m.id > 0).map(m => m.id as number)
      const toDelete = existingMappings.filter(m => !existingIds.includes(m.id))
      
      for (const mapping of toDelete) {
        try {
          await discordApiService.deleteChannelMapping(mapping.id)
        } catch (err) {
          console.error('Erro ao deletar mapeamento:', err)
        }
      }

      // Cria/atualiza mapeamentos
      for (const mapping of validMappings) {
        try {
          if (mapping.id === null || mapping.id < 0) { // ID negativo = novo (temporário)
            // Busca o nome do canal
            const channel = channels.find(c => c.id === mapping.channelId)
            const channelName = channel ? channel.name : mapping.channelName || 'Unknown'
            
            // Novo mapeamento
            await discordApiService.createChannelMapping({
              guild_id: selectedGuildId,
              channel_id: mapping.channelId,
              channel_name: channelName,
              project_id: mapping.projectId!,
              task_creation_mode: mapping.taskCreationMode
            })
          } else {
            // Atualiza mapeamento existente
            const channel = channels.find(c => c.id === mapping.channelId)
            const channelName = channel ? channel.name : mapping.channelName || 'Unknown'
            
            await discordApiService.updateChannelMapping(mapping.id, {
              project_id: mapping.projectId!,
              task_creation_mode: mapping.taskCreationMode,
              channel_name: channelName
            })
          }
        } catch (err: any) {
          console.error('Erro ao salvar mapeamento:', err)
          throw new Error(err.response?.data?.message || 'Erro ao salvar mapeamento')
        }
      }

      onSuccess()
      handleClose()
    } catch (err: any) {
      console.error('Erro ao salvar configurações:', err)
      setError(err.message || 'Erro ao salvar configurações')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o Discord?')) {
      return
    }

    setIsDisconnecting(true)
    setError(null)
    
    try {
      await discordApiService.disconnect()
      setMappings([])
      onSuccess() // Atualiza o card
      onClose() // Fecha o modal
    } catch (err: any) {
      console.error('Erro ao desconectar:', err)
      setError(err.response?.data?.message || 'Erro ao desconectar Discord')
    } finally {
      setIsDisconnecting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setMappings([])
      setExistingMappings([])
      setChannels([])
      setSelectedGuildId('')
      setSelectedGuildName('')
      setError(null)
      onClose()
    }
  }

  const handleCreateProjectAndClient = (newProjectName: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('new_project_name', newProjectName)
    setSearchParams(newParams)
    setShowProjectAndClientModal(true)
  }

  return (
    <>
    <Modal 
      show={show} 
      onHide={handleClose}
      size="lg"
      backdrop={isSubmitting || isDisconnecting ? 'static' : true}
      keyboard={!isSubmitting && !isDisconnecting}
      className="discord-manage-modal"
    >
      <Modal.Header closeButton={!isSubmitting && !isDisconnecting}>
        <Modal.Title>
          <i className='fab fa-discord text-primary me-2'></i>
          Gerenciar integração Discord
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {isLoading && mappings.length === 0 ? (
          <div className='text-center py-5'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Carregando...</span>
            </div>
            <p className='text-muted mt-3'>Carregando dados...</p>
          </div>
        ) : (
          <>
            {/* Seção: Informações gerais */}
            {selectedGuildId && (
              <div className='mb-6'>
                <h5 className='mb-4'>Informações gerais</h5>
                <div className='d-flex flex-column gap-3'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <span className='text-muted'>Servidor:</span>
                    <span className='fw-semibold'>{selectedGuildName || guildName || 'N/A'}</span>
                  </div>
                  <div className='d-flex align-items-center justify-content-between'>
                    <span className='text-muted'>ID do servidor:</span>
                    <span className='text-muted'>{selectedGuildId}</span>
                  </div>
                  <div className='d-flex align-items-center justify-content-between'>
                    <span className='text-muted'>Status:</span>
                    <span className='badge badge-light-success'>Conectado</span>
                  </div>
                </div>
              </div>
            )}

            <div className='separator my-6'></div>

            {/* Seção: Seleção de servidor */}
            <div className='mb-4'>
              <Form.Group>
                <Form.Label>
                  <strong>Servidor Discord</strong> {!selectedGuildId && <span className='text-danger'>*</span>}
                </Form.Label>
                {guilds.length > 0 ? (
                  <Form.Select
                    key={selectedGuildId || 'empty'}
                    value={selectedGuildId}
                    onChange={(e) => handleGuildChange(e.target.value)}
                    disabled={isSubmitting || isDisconnecting || isLoading}
                  >
                    <option value=''>Selecione um servidor</option>
                    {guilds.map(guild => (
                      <option key={guild.id} value={guild.id}>
                        {guild.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <div className='text-muted p-3 bg-light rounded'>
                    {isLoading ? 'Carregando servidores...' : 'Nenhum servidor disponível. Verifique se você tem acesso a servidores Discord.'}
                  </div>
                )}
                {!selectedGuildId && guilds.length > 0 && (
                  <Form.Text className='text-body' style={{opacity: 0.75, fontSize: '1rem'}}>
                    Selecione o servidor Discord que deseja conectar ao TaskRush
                  </Form.Text>
                )}
              </Form.Group>
            </div>

            {error && (
              <Alert variant='danger' className='mb-4'>
                <i className='fas fa-exclamation-triangle me-2'></i>
                <div>
                  <strong>Erro ao carregar canais:</strong>
                  <p className='mb-0 mt-2'>{error}</p>
                  {(error.includes('Bot não tem permissão') || error.includes('bot não está no servidor') || error.includes('Missing Access')) ? (
                    <div className='mt-3'>
                      <p className='mb-2'><strong>⚠️ Importante:</strong> Você precisa ser <strong>administrador do servidor Discord</strong> para fazer esta integração funcionar.</p>
                      <p className='mb-2'><strong>Como resolver:</strong></p>
                      <ol className='mb-0' style={{paddingLeft: '1.5rem'}}>
                        <li>Certifique-se de que você é <strong>administrador</strong> do servidor Discord selecionado</li>
                        <li>No Discord Developer Portal, vá na aba <strong>"OAuth2"</strong></li>
                        <li>Na seção <strong>"URL Generator"</strong>, selecione os escopos: <code>bot</code> e <code>applications.commands</code></li>
                        <li>Selecione as permissões: <code>View Channels</code>, <code>Read Message History</code></li>
                        <li>Copie a URL gerada e abra em uma nova aba</li>
                        <li>Selecione o servidor desejado e autorize o bot</li>
                        <li>Volte aqui e tente novamente</li>
                      </ol>
                    </div>
                  ) : null}
                </div>
              </Alert>
            )}

            {/* Seção: Mapeamento de canais */}
            <div className='mb-6'>
              <div className='d-flex align-items-center justify-content-between mb-4'>
                <h5 className='mb-0'>Mapeamento de canais para projetos</h5>
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={handleAddMapping}
                  disabled={isSubmitting || isDisconnecting || !selectedGuildId}
                >
                  <i className='fas fa-plus me-2'></i>
                  Adicionar mapeamento
                </Button>
              </div>

              {mappings.length === 0 ? (
                <div className='text-center py-5 text-muted'>
                  <i className='fas fa-inbox fs-2x mb-3 d-block'></i>
                  <p>Nenhum mapeamento configurado</p>
                  <p>
                    {!selectedGuildId
                      ? 'Selecione um servidor para começar'
                      : 'Clique em "Adicionar mapeamento" para começar'
                    }
                  </p>
                </div>
              ) : (
                <div className='table-responsive'>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Canal Discord</th>
                        <th>Projeto no TaskRush</th>
                        <th style={{width: '50px'}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.map((mapping, index) => (
                        <tr key={mapping.id || `new-${index}`}>
                          <td>
                              <Select
                                key={`channel-select-${mapping.id}-${channels.length}`}
                                value={mapping.channelId ? {
                                  value: mapping.channelId,
                                  label: `#${mapping.channelName}`
                                } : null}
                                onChange={(newValue: SingleValue<{value: string; label: string}>) => {
                                  if (newValue) {
                                    handleMappingChange(mapping.id, 'channelId', newValue.value)
                                  } else {
                                    handleMappingChange(mapping.id, 'channelId', '')
                                  }
                                }}
                                options={getAvailableChannelsForMapping(mapping.id, mapping.channelId).map(channel => ({
                                  value: String(channel.id),
                                  label: `#${channel.name}`
                                }))}
                                placeholder={!selectedGuildId 
                                  ? 'Selecione um servidor primeiro'
                                  : isLoading
                                    ? 'Carregando canais...'
                                    : channels.length === 0
                                      ? 'Nenhum canal disponível'
                                      : getAvailableChannelsForMapping(mapping.id, mapping.channelId).length === 0
                                        ? 'Todos os canais já estão mapeados'
                                      : 'Selecione um canal'
                                }
                                isDisabled={isSubmitting || isDisconnecting || !selectedGuildId || isLoading}
                                noOptionsMessage={() => {
                                  if (!selectedGuildId) return 'Selecione um servidor primeiro'
                                  if (isLoading) return 'Carregando canais...'
                                  if (channels.length === 0) return 'Nenhum canal disponível'
                                  if (getAvailableChannelsForMapping(mapping.id, mapping.channelId).length === 0) {
                                    return 'Todos os canais já estão mapeados'
                                  }
                                  return 'Digite para buscar...'
                                }}
                                isLoading={isLoading}
                                isSearchable
                                isClearable
                                className='discord-channel-select'
                                classNamePrefix='discord-select'
                                menuPortalTarget={document.body}
                              />
                          </td>
                          <td>
                            <div className='discord-project-select-wrapper'>
                              <SelectAutoComplete
                                endPoint='projetos'
                                value={mapping.projectId ? {
                                  value: mapping.projectId.toString(),
                                  label: mapping.projectName || ''
                                } : undefined}
                                placeholder='Selecione um projeto'
                                onChange={(newValue) => {
                                  handleMappingChange(mapping.id, 'projectId', newValue)
                                }}
                                onCreateOption={(inputValue) => handleCreateProjectAndClient(inputValue)}
                                isDisabled={isSubmitting || isDisconnecting}
                              />
                            </div>
                          </td>
                          <td className='text-center'>
                            <Button
                              variant='link'
                              size='sm'
                              onClick={() => handleRemoveMapping(mapping.id)}
                              disabled={isSubmitting || isDisconnecting}
                              className='text-danger p-0'
                            >
                              <i className='fas fa-trash'></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <div className='mt-4'>
                <p className='text-muted mb-0'>
                  <i className='fas fa-info-circle me-2'></i>
                  Quando um canal do Discord estiver mapeado para um projeto, ficará mais fácil criar tarefas a partir das conversas da equipe. As regras de criação automática serão habilitadas em fases futuras.
                </p>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button
          variant='link'
          className='text-danger'
          onClick={handleDisconnect}
          disabled={isSubmitting || isDisconnecting}
        >
          {isDisconnecting ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
              Desconectando...
            </>
          ) : (
            <>
              <i className='fas fa-unlink me-2'></i>
              Desconectar Discord
            </>
          )}
        </Button>
        <div className='ms-auto'>
          <Button
            variant='secondary'
            onClick={handleClose}
            disabled={isSubmitting || isDisconnecting}
            className='me-2'
          >
            Cancelar
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading || isDisconnecting}
          >
            {isSubmitting ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                Salvando...
              </>
            ) : (
              <>
                <i className='fas fa-save me-2'></i>
                Salvar configurações
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
          <CreateProjectAndClient
            show={showProjectAndClientModal}
            handleClose={() => setShowProjectAndClientModal(false)}
          />
          </>
  )
}

export default DiscordManageModal

